import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  loginRequestSchema,
  loginResponseSchema,
  meResponseSchema,
  refreshResponseSchema,
  type LoginRequest,
  type UserInfo,
} from '@app/shared';
import { ApiPath, apiGet, apiPost } from '../../lib/api';

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: UserInfo | null;
}

interface AuthContextValue extends AuthState {
  login: (request: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('idle');
  const [user, setUser] = useState<UserInfo | null>(null);

  const fetchMe = useCallback(async () => {
    try {
      const data = await apiGet(ApiPath.AUTH.ME, meResponseSchema);
      setUser({
        pegawai_id: data.pegawai.id,
        nama: data.pegawai.nama,
        username: data.akun.username,
        email: data.akun.email,
        roles: data.roles,
      });
      setStatus('authenticated');
      return true;
    } catch {
      setUser(null);
      setStatus('unauthenticated');
      return false;
    }
  }, []);

  useEffect(() => {
    // Coba restore session saat aplikasi dimuat.
    void fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (request: LoginRequest) => {
    loginRequestSchema.parse(request);
    const data = await apiPost(ApiPath.AUTH.LOGIN, request, loginResponseSchema);
    setUser(data.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost(ApiPath.AUTH.LOGOUT, {}, refreshResponseSchema);
    } catch {
      // Tetap bersihkan state meskipun server gagal.
    } finally {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      await apiPost(ApiPath.AUTH.REFRESH, {}, refreshResponseSchema);
      return fetchMe();
    } catch {
      setUser(null);
      setStatus('unauthenticated');
      return false;
    }
  }, [fetchMe]);

  const value = useMemo(
    () => ({ status, user, login, logout, refresh }),
    [status, user, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
